import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, ExternalLink, Clock, CheckCircle2, AlertTriangle,
  XCircle, RefreshCw, ClipboardCheck, FileText, Shield, TicketCheck,
  FileUp, AlertCircle, Activity, BellRing
} from "lucide-react";
import { subscribeToPush, isPushSupported, getPushPermissionState } from "../utils/pushSubscription";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICONS = {
  "client_onboarding.run_assigned": { icon: ClipboardCheck, color: "#36A9E1" },
  "client_onboarding.response_submitted": { icon: Clock, color: "#38BDF8" },
  "client_onboarding.revision_requested": { icon: RefreshCw, color: "#FBBF24" },
  "client_onboarding.submission_accepted": { icon: CheckCircle2, color: "#4ADE80" },
  "client_onboarding.submission_rejected": { icon: XCircle, color: "#F87171" },
  "governance.assessment_created": { icon: AlertTriangle, color: "#A78BFA" },
  "governance.remediation_overdue": { icon: AlertTriangle, color: "#F87171" },
  "capa.created": { icon: AlertCircle, color: "#F59E0B" },
  "capa.status_changed": { icon: Activity, color: "#36A9E1" },
  "capa.assigned": { icon: ClipboardCheck, color: "#36A9E1" },
  "capa.overdue": { icon: AlertTriangle, color: "#F87171" },
  "audit.created": { icon: Shield, color: "#A78BFA" },
  "audit.started": { icon: Shield, color: "#36A9E1" },
  "audit.completed": { icon: CheckCircle2, color: "#4ADE80" },
  "audit.finding.added": { icon: AlertTriangle, color: "#FBBF24" },
  "audit.overdue": { icon: AlertTriangle, color: "#F87171" },
  "deviation.submitted": { icon: AlertCircle, color: "#F59E0B" },
  "deviation.status_changed": { icon: Activity, color: "#36A9E1" },
  "support.ticket_created": { icon: TicketCheck, color: "#36A9E1" },
  "support.ticket_updated": { icon: TicketCheck, color: "#4ADE80" },
  "doc_control.uploaded": { icon: FileUp, color: "#36A9E1" },
  "doc_control.revised": { icon: FileText, color: "#A78BFA" },
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [sseConnected, setSseConnected] = useState(false);
  const [pushState, setPushState] = useState("default"); // default | granted | denied | unsupported
  const panelRef = useRef(null);
  const eventSourceRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pushSubscribed = useRef(false);

  // SSE Connection with auto-reconnect
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(`${API}/api/events/stream`, { withCredentials: true });
    eventSourceRef.current = es;

    es.addEventListener("connected", () => {
      setSseConnected(true);
      reconnectAttempts.current = 0;
    });

    es.addEventListener("heartbeat", () => {
      // Keep-alive acknowledged
    });

    // Listen for all notification event types
    const eventTypes = Object.keys(EVENT_ICONS);
    eventTypes.forEach((eventType) => {
      es.addEventListener(eventType, (e) => {
        try {
          const data = JSON.parse(e.data);
          setEvents((prev) => {
            const updated = [data, ...prev].slice(0, 10);
            return updated;
          });
          setUnread((prev) => prev + 1);
        } catch (err) {
          // silent parse error
        }
      });
    });

    es.onerror = () => {
      setSseConnected(false);
      es.close();
      eventSourceRef.current = null;

      // Exponential backoff reconnect: 2s, 4s, 8s, 16s, max 30s
      const delay = Math.min(2000 * Math.pow(2, reconnectAttempts.current), 30000);
      reconnectAttempts.current += 1;

      reconnectTimerRef.current = setTimeout(() => {
        connectSSE();
      }, delay);
    };
  }, []);

  useEffect(() => {
    connectSSE();
    // Auto-subscribe to push after SSE connects
    if (!pushSubscribed.current && isPushSupported()) {
      getPushPermissionState().then((state) => {
        setPushState(state);
        if (state === "granted") {
          subscribeToPush().then(() => { pushSubscribed.current = true; });
        }
      });
    }
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [connectSSE]);

  const handleEnablePush = async () => {
    const sub = await subscribeToPush();
    if (sub) {
      setPushState("granted");
      pushSubscribed.current = true;
    } else {
      const p = await getPushPermissionState();
      setPushState(p);
    }
  };

  // Fetch unread count on mount and periodically as fallback
  const fetchUnread = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/unread-count`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUnread(data.unread_count || 0);
      }
    } catch (e) { /* silent */ }
  }, []);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/my-feed?limit=10`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        setTotal(data.total || 0);
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    // Fallback polling every 60s (SSE handles real-time)
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchFeed();
  }, [open, fetchFeed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const markRead = async (eventId) => {
    await fetch(`${API}/api/notifications/${eventId}/read`, { method: "PATCH", credentials: "include" });
    setEvents(prev => prev.map(e => e.event_id === eventId ? { ...e, status: "read" } : e));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/mark-all-read`, { method: "POST", credentials: "include" });
    setEvents(prev => prev.map(e => ({ ...e, status: "read" })));
    setUnread(0);
  };

  const handleClick = (evt) => {
    if (evt.status === "unread") markRead(evt.event_id);
    if (evt.link) {
      navigate(evt.link);
      setOpen(false);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-[rgba(255,255,255,0.06)] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} className="text-[#8B95A5]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#DC2626] text-white text-[10px] font-bold px-1" data-testid="unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {sseConnected && (
          <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-[#4ADE80]" title="Real-time connected" data-testid="sse-indicator" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[480px] bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded-lg shadow-2xl z-50 flex flex-col" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(255,255,255,0.06)]">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold text-sm">Notifications</h3>
              {sseConnected && (
                <span className="text-[9px] text-[#4ADE80] bg-[rgba(74,222,128,0.1)] px-1.5 py-0.5 rounded" data-testid="sse-live-badge">LIVE</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[10px] text-[#36A9E1] hover:text-white" data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {pushState === "default" && isPushSupported() && (
            <div className="px-4 py-2 border-b border-[rgba(255,255,255,0.06)] bg-[rgba(54,169,225,0.06)]">
              <button
                onClick={handleEnablePush}
                className="flex items-center gap-2 text-xs text-[#36A9E1] hover:text-white w-full"
                data-testid="enable-push-btn"
              >
                <BellRing size={14} />
                Enable push notifications for background alerts
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {events.length === 0 ? (
              <div className="text-center py-10 text-[#8B95A5] text-sm" data-testid="no-notifications">
                <Bell size={28} className="mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : (
              events.map(evt => {
                const meta = EVENT_ICONS[evt.event_type] || { icon: Bell, color: "#8B95A5" };
                const Icon = meta.icon;
                const isUnread = evt.status === "unread";
                return (
                  <div
                    key={evt.event_id}
                    onClick={() => handleClick(evt)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-[rgba(255,255,255,0.03)] ${isUnread ? "bg-[rgba(54,169,225,0.04)] hover:bg-[rgba(54,169,225,0.08)]" : "hover:bg-[rgba(255,255,255,0.03)]"}`}
                    data-testid={`notification-${evt.event_id}`}
                  >
                    <div className="shrink-0 mt-0.5">
                      <Icon size={16} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium truncate ${isUnread ? "text-white" : "text-[#8B95A5]"}`}>{evt.title}</span>
                        {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-[#36A9E1] shrink-0" />}
                      </div>
                      <p className="text-[11px] text-[#8B95A5] mt-0.5 line-clamp-2">{evt.message}</p>
                      <span className="text-[10px] text-[#555] mt-1 block">{timeAgo(evt.created_at)}</span>
                    </div>
                    {evt.link && (
                      <ExternalLink size={12} className="shrink-0 mt-1 text-[#555]" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {total > 10 && (
            <div className="border-t border-[rgba(255,255,255,0.06)] px-4 py-2.5">
              <button
                onClick={() => { navigate("/notifications"); setOpen(false); }}
                className="text-xs text-[#36A9E1] hover:text-white flex items-center gap-1 w-full justify-center"
                data-testid="view-all-notifications"
              >
                <ExternalLink size={12} /> View All ({total})
              </button>
            </div>
          )}
          {total <= 10 && total > 0 && (
            <div className="border-t border-[rgba(255,255,255,0.06)] px-4 py-2.5">
              <button
                onClick={() => { navigate("/notifications"); setOpen(false); }}
                className="text-xs text-[#36A9E1] hover:text-white flex items-center gap-1 w-full justify-center"
                data-testid="view-all-notifications"
              >
                <ExternalLink size={12} /> View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
